<script lang="ts">
  import { onMount } from 'svelte';
  import { worldStore, dispatch, hardReset, getWorld } from '@sim/store';
  import { STORAGE_KEY } from '@sim/save';
  import { alerts } from '@sim/selectors';
  import StartMenu from '@ui-sim/StartMenu.svelte';
  import TopBar from '@ui-sim/TopBar.svelte';
  import Tower from '@ui-sim/Tower.svelte';
  import CityCanvas from '@ui-sim/CityCanvas.svelte';
  import ColonistInspector from '@ui-sim/ColonistInspector.svelte';
  import RoomInspector from '@ui-sim/RoomInspector.svelte';
  import BuildMenu from '@ui-sim/BuildMenu.svelte';
  import Drawer from '@ui-sim/Drawer.svelte';
  import RaidModal from '@ui-sim/RaidModal.svelte';
  import RecruitCard from '@ui-sim/RecruitCard.svelte';
  import ProgressBanner from '@ui-sim/ProgressBanner.svelte';
  import EmpirePanel from '@ui-sim/EmpirePanel.svelte';
  import { activeBuilding } from '@sim/world';
  import { isNight } from '@sim/config';
  import { music, initMusicOnGesture } from '@ui-sim/music';
  import { isRainy } from '@ui-sim/weather';

  let view: 'building' | 'city' = 'building';
  let selectedColonist: number | null = null;
  let selectedRoom: number | null = null;
  let buildTarget: { floor: number; slot: number } | null = null;
  let openTab: string | null = null;

  // desktop = wide window → use a full-screen stage + rail layout
  let isDesktop = false;

  // Start menu: shown over the live city until the player picks New Game / Continue.
  let menuOpen = true;
  let hasSave = false;
  let resumeSpeed = 1;

  onMount(() => {
    const mq = window.matchMedia('(min-width: 1000px)');
    const update = () => (isDesktop = mq.matches);
    update();
    mq.addEventListener('change', update);

    // Detect a resumable save, show the city behind the menu, and pause the clock until start.
    try {
      hasSave = !!localStorage.getItem(STORAGE_KEY);
    } catch {
      hasSave = false;
    }
    resumeSpeed = getWorld().speed || 1;
    view = 'city';
    dispatch({ type: 'setSpeed', speed: 0 });

    initMusicOnGesture(); // ambient soundtrack boots on the first tap/keypress

    return () => mq.removeEventListener('change', update);
  });

  function startNew() {
    hardReset();
    menuOpen = false;
    dispatch({ type: 'setSpeed', speed: 1 });
  }
  function continueGame() {
    menuOpen = false;
    dispatch({ type: 'setSpeed', speed: resumeSpeed > 0 ? resumeSpeed : 1 });
  }

  function clearSheets() {
    selectedColonist = null;
    selectedRoom = null;
    buildTarget = null;
  }
  function selColonist(id: number) {
    clearSheets();
    selectedColonist = id;
    if (!isDesktop) openTab = null;
  }
  function selRoom(id: number) {
    clearSheets();
    selectedRoom = id;
  }
  function buildAt(e: CustomEvent<{ floor: number; slot: number }>) {
    clearSheets();
    buildTarget = e.detail;
  }
  function jumpToBuilding(buildingId: number) {
    clearSheets();
    openTab = null;
    dispatch({ type: 'switchBuilding', buildingId });
    view = 'building';
  }

  $: city = $worldStore;
  $: b = activeBuilding(city);
  $: hasBuilding = city.buildings.length > 0;
  $: if (!hasBuilding && view !== 'city') view = 'city';
  // The 3D city stays mounted whenever it's the active view; the building views render over it.
  // Keeping CityCanvas alive (and just paused) avoids rebuilding the whole scene on every switch.
  $: showCity = view === 'city' || !hasBuilding;
  // ambient track follows the scene + the in-game clock (map/building × day/night)
  $: music.setScene(`${showCity ? 'map' : 'bld'}-${isNight(city.t) ? 'night' : 'day'}`);
  $: music.setRain(isRainy(city.t));
  $: al = alerts(city);
  $: sheetOpen = selectedColonist != null || selectedRoom != null || buildTarget != null;
  // the persistent rail always shows a tab on desktop
  $: railTab = openTab ?? 'crew';

  const tabs = [
    ['crew', '👥', 'Crew'],
    ['build', '🏗️', 'Build'],
    ['security', '🛡️', 'Security'],
    ['log', '📜', 'Log'],
  ];
</script>

<div class="app" class:desktop={isDesktop}>
  <TopBar onCity={() => { clearSheets(); openTab = null; view = 'city'; }} />

  <!-- cross-building alerts (idle crew, raids elsewhere, ...) — visible from the map too,
       so a raid on a building you're not managing doesn't go unnoticed. -->
  {#if al.length}
    <div class="alerts">
      {#each al.slice(0, 3) as a}
        {@const jt = a.jumpTo}
        {#if jt != null}
          <button class="alert {a.kind} clickable" on:click={() => jumpToBuilding(jt)}>⚠ {a.text}</button>
        {:else}
          <span class="alert {a.kind}">⚠ {a.text}</span>
        {/if}
      {/each}
    </div>
  {/if}

  <!-- toasts: transient cross-building notices (raid started/resolved elsewhere) -->
  {#if city.toasts.length}
    <div class="toasts">
      {#each city.toasts as t (t.id)}
        <div class="toast {t.kind}">
          <span class="ttext">{t.text}</span>
          {#if t.buildingId !== b?.id}
            <button class="tjump" on:click={() => { jumpToBuilding(t.buildingId); dispatch({ type: 'dismissToast', id: t.id }); }}>Jump →</button>
          {/if}
          <button class="tx" on:click={() => dispatch({ type: 'dismissToast', id: t.id })} aria-label="Dismiss">✕</button>
        </div>
      {/each}
    </div>
  {/if}

  <!-- CITY: one persistent canvas instance, always mounted (hidden + paused inside a building so
       the 3D scene is never torn down / rebuilt). Empire rail appears beside it on desktop. -->
  <div class="workspace cityws" class:hidden={!showCity} class:solo={!(isDesktop && hasBuilding)}>
    <section class="stagecol">
      <main class="stage citystage">
        <CityCanvas paused={!showCity} on:enter={() => (view = 'building')} />
      </main>
    </section>
    {#if isDesktop && hasBuilding}
      <aside class="rail">
        <EmpirePanel on:enter={() => (view = 'building')} />
      </aside>
    {/if}
  </div>

  {#if !showCity}
    {#if isDesktop}
    <!-- DESKTOP: stage + persistent management rail -->
    <div class="workspace">
      <section class="stagecol">
        <main class="stage">
          <div class="stagewrap">
            <Tower
              {selectedColonist}
              {selectedRoom}
              on:selectColonist={(e) => selColonist(e.detail)}
              on:selectRoom={(e) => selRoom(e.detail)}
              on:buildAt={buildAt}
            />
          </div>
        </main>
        <ProgressBanner />
      </section>
      <aside class="rail">
        <Drawer
          docked
          tab={railTab}
          on:tab={(e) => { clearSheets(); openTab = e.detail; }}
          on:selectColonist={(e) => selColonist(e.detail)}
        />
      </aside>
    </div>
  {:else}
    <!-- MOBILE: single column with bottom nav + sheets -->
    <main>
      <Tower
        {selectedColonist}
        {selectedRoom}
        on:selectColonist={(e) => selColonist(e.detail)}
        on:selectRoom={(e) => selRoom(e.detail)}
        on:buildAt={buildAt}
      />
    </main>

    <ProgressBanner />

    <nav class="bottom">
      <button class="nav city" on:click={() => { clearSheets(); view = 'city'; }}>
        <span class="ni">🏙️</span><span class="nl">City</span>
      </button>
      {#each tabs as [id, icon, label]}
        <button class="nav" class:on={openTab === id} on:click={() => { clearSheets(); openTab = id; }}>
          <span class="ni">{icon}</span><span class="nl">{label}</span>
        </button>
      {/each}
    </nav>
    {/if}
  {/if}

  <!-- inspectors: bottom-sheet on mobile, centred modal on desktop -->
  {#if sheetOpen}
    <div class="scrim" on:click|self={clearSheets} role="presentation">
      <div class="dock">
        {#if selectedColonist != null}
          <ColonistInspector id={selectedColonist} on:close={clearSheets} />
        {:else if selectedRoom != null}
          <RoomInspector id={selectedRoom} on:close={clearSheets} on:selectColonist={(e) => selColonist(e.detail)} />
        {:else if buildTarget}
          <BuildMenu floor={buildTarget.floor} slot={buildTarget.slot} on:close={clearSheets} />
        {/if}
      </div>
    </div>
  {/if}

  <!-- mobile drawer overlay (desktop uses the persistent rail instead) -->
  {#if !isDesktop && openTab}
    <Drawer tab={openTab} on:close={() => (openTab = null)} on:tab={(e) => (openTab = e.detail)} on:selectColonist={(e) => selColonist(e.detail)} />
  {/if}

  {#if city.recruit}<RecruitCard />{/if}
  {#if b?.raid}<RaidModal />{/if}

  {#if menuOpen}
    <StartMenu {hasSave} on:newgame={startNew} on:continue={continueGame} />
  {/if}
</div>

<style>
  .app {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: 560px;
    margin: 0 auto;
    background: var(--bg);
    overflow: hidden;
  }
  @media (min-width: 640px) {
    .app {
      max-width: 760px;
      border-left: 1px solid var(--line);
      border-right: 1px solid var(--line);
    }
  }

  /* ---- desktop: fill the window, stage + rail ---- */
  .app.desktop {
    max-width: none;
    border-left: none;
    border-right: none;
  }
  .app.desktop .citywrap {
    max-width: 860px;
    margin: 0 auto;
  }
  /* keep the tower a sensible width on a wide stage instead of stretching;
     full height so Tower.svelte can measure the space it actually has to fill */
  .app.desktop .stagewrap {
    max-width: 720px;
    width: 100%;
    height: 100%;
    margin: 0 auto;
  }
  .workspace {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(340px, 420px);
    overflow: hidden;
  }
  /* city view with no empire rail (mobile, or no buildings yet) = single column */
  .workspace.solo {
    grid-template-columns: 1fr;
  }
  /* the persistent city canvas is hidden (not unmounted) while inside a building */
  .workspace.cityws.hidden {
    display: none;
  }
  .stagecol {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }
  .rail {
    display: flex;
    min-height: 0;
    border-left: 1px solid var(--line);
    background: var(--bg-2);
  }
  .app.desktop .scrim {
    align-items: center;
    justify-content: center;
  }
  .app.desktop .dock {
    width: auto;
    max-width: 460px;
  }

  .alerts {
    display: flex;
    gap: 6px;
    padding: 5px 10px;
    overflow-x: auto;
    background: #14141d;
    border-bottom: 1px solid var(--line);
  }
  .alert {
    white-space: nowrap;
    font-size: 10.5px;
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--panel-2);
    border: 1px solid var(--line);
  }
  .alert.warn {
    color: var(--heat-warm);
    border-color: color-mix(in srgb, var(--heat-warm) 35%, var(--line));
  }
  .alert.bad {
    color: var(--bad);
    border-color: color-mix(in srgb, var(--bad) 40%, var(--line));
  }
  .alert.clickable {
    font: inherit;
    color: inherit;
    cursor: pointer;
  }
  .alert.clickable:hover {
    border-color: var(--ink-dim);
  }

  .toasts {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 45;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 260px;
  }
  .toast {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 8px;
    border-radius: 9px;
    background: var(--panel);
    border: 1px solid var(--line);
    box-shadow: var(--shadow);
    animation: rise 0.2s ease both;
  }
  .toast.raid {
    border-color: var(--heat-crit);
  }
  .toast.good {
    border-color: var(--good);
  }
  .ttext {
    flex: 1;
    font-size: 11px;
    color: var(--ink);
  }
  .tjump {
    font-size: 10.5px;
    font-weight: 700;
    padding: 3px 7px;
    border-radius: 6px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink);
    white-space: nowrap;
  }
  .tx {
    font-size: 11px;
    color: var(--ink-faint);
    padding: 2px 4px;
  }
  main {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
  /* the PixiJS city canvas fills its stage instead of scrolling */
  .citystage {
    overflow: hidden;
    padding: 10px;
  }
  .bottom {
    display: flex;
    gap: 4px;
    padding: 6px 8px calc(6px + env(safe-area-inset-bottom));
    background: var(--panel);
    border-top: 1px solid var(--line);
  }
  .nav {
    flex: 1;
    min-height: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    border-radius: 9px;
    background: none;
    border: none;
    color: var(--ink-dim);
  }
  .nav.on {
    background: var(--panel-2);
    color: var(--ink);
  }
  .nav.city {
    color: var(--era-accent, #39d353);
  }
  .ni {
    font-size: 17px;
  }
  .nl {
    font-size: 9.5px;
  }
  .scrim {
    position: absolute;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: flex-end;
    background: rgba(6, 6, 12, 0.5);
  }
  .dock {
    width: 100%;
    padding: 8px;
    animation: rise 0.2s ease both;
  }
</style>
